/**
 * SYRE lifetime-deal state for /syre and /signup?offer=syre-lifetime (F7 v2).
 *
 * Open/closed comes from `GET /api/offers/syre-lifetime` (B23 offer windows,
 * so a new window reopens the page with no deploy), falling back to
 * `isLifetimeOpen()` from `@shared/pricing` when the API is down. The page
 * re-evaluates on a timer and closes itself at `closesAt`.
 *
 * Testing: `?now=<ISO date>` overrides the clock, but only outside
 * production (dev builds, or any host other than padpulse.io). While an
 * override is active the API is ignored, so the closed state can be previewed.
 */
import { PRICING } from "@shared/pricing";

export const SYRE_OFFER_ENDPOINT = "/api/offers/syre-lifetime";
export const LIFETIME_CHECKOUT_ENDPOINT = "/api/billing/lifetime-checkout";

const PRODUCTION_HOSTS = new Set(["padpulse.io", "www.padpulse.io"]);

export interface SyreOffer {
  eventId?: number;
  presentationUrl?: string | null;
  qaUrl?: string | null;
  qaClosesAt?: string | null;
  promotionLabel?: string | null;
  bonusClosesAt?: string | null;
  refundFloorAt?: string | null;
  eventTerms?: string | null;
  standardLocks?: boolean;
  open: boolean;
  /** ISO close time of the current window (open state only). */
  closesAt: string | null;
  priceCents: number;
  propertyAllowancePerUnit: number;
  maxUnits: number;
  maxTotalAllowance: number;
  /** Most recent webinar replay (shown in both states when present). */
  webinarReplayUrl: string | null;
  webinarTitle: string | null;
  /** Closed state: when the next window opens, if one is scheduled. */
  nextOpensAt: string | null;
}

export function timeOverrideAllowed(hostname: string, isDev: boolean): boolean {
  return isDev || !PRODUCTION_HOSTS.has(hostname.toLowerCase());
}

/** Parses `?now=` when overrides are allowed; returns null otherwise. */
export function parseTimeOverride(search: string, hostname: string, isDev: boolean): Date | null {
  if (!timeOverrideAllowed(hostname, isDev)) return null;
  const raw = new URLSearchParams(search).get("now");
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const lt = PRICING.lifetime;

export function fallbackOffer(_now: Date): SyreOffer {
  const open = false; // A network failure must not reopen a campaign.
  return {
    open,
    closesAt: open ? new Date(lt.defaultClosesAt).toISOString() : null,
    priceCents: lt.priceOneTime,
    propertyAllowancePerUnit: lt.propertyAllowance,
    maxUnits: lt.maxUnits,
    maxTotalAllowance: lt.maxTotalAllowance,
    webinarReplayUrl: null,
    webinarTitle: null,
    nextOpensAt: null,
  };
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const numOr = (v: unknown, d: number): number => (typeof v === "number" && Number.isFinite(v) ? v : d);

/** Normalizes the API payload (open or between-windows shape); null when unusable. */
export function parseSyreOffer(data: unknown): SyreOffer | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (typeof d.open !== "boolean") return null;
  const closesAt = d.open ? str(d.closesAt) : null;
  if (d.open && !closesAt) return null;
  return {
    open: d.open,
    eventId: typeof d.eventId === "number" ? d.eventId : undefined,
    presentationUrl: str(d.presentationUrl),
    qaUrl: str(d.qaUrl),
    qaClosesAt: str(d.qaClosesAt),
    promotionLabel: str(d.promotionLabel),
    bonusClosesAt: str(d.bonusClosesAt),
    refundFloorAt: str(d.refundFloorAt),
    eventTerms: str(d.eventTerms),
    standardLocks: d.standardLocks === true,
    closesAt,
    priceCents: numOr(d.priceCents, lt.priceOneTime),
    propertyAllowancePerUnit: numOr(d.propertyAllowancePerUnit, lt.propertyAllowance),
    maxUnits: numOr(d.maxUnits, lt.maxUnits),
    maxTotalAllowance: numOr(d.maxTotalAllowance, lt.maxTotalAllowance),
    webinarReplayUrl: str(d.webinarReplayUrl),
    webinarTitle: str(d.webinarTitle),
    nextOpensAt: str(d.nextOpensAt),
  };
}

/** Reads the offer from the API; null when the endpoint isn't available or returns something unexpected. */
export async function fetchSyreOffer(): Promise<SyreOffer | null> {
  try {
    const res = await fetch(SYRE_OFFER_ENDPOINT, { credentials: "include" });
    if (!res.ok) return null;
    return parseSyreOffer(await res.json());
  } catch {
    return null;
  }
}

/** The unit choices on the picker: 1..maxUnits → 5/10/15/20 properties. */
export function unitOptions(offer: Pick<SyreOffer, "maxUnits" | "propertyAllowancePerUnit" | "priceCents">): Array<{ units: number; properties: number; priceCents: number }> {
  return Array.from({ length: Math.max(1, offer.maxUnits) }, (_, i) => ({
    units: i + 1,
    properties: (i + 1) * offer.propertyAllowancePerUnit,
    priceCents: (i + 1) * offer.priceCents,
  }));
}

/** Clamps a `?units=` value to 1..maxUnits (default 1). */
export function parseUnits(raw: string | null | undefined, maxUnits: number = lt.maxUnits): number {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return 1;
  return Math.min(n, maxUnits);
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
}

/** Whole days/hours/minutes from `now` until `closesAt` (zeros once past). */
export function countdownTo(closesAt: string, now: Date): Countdown {
  const ms = Math.max(0, new Date(closesAt).getTime() - now.getTime());
  const totalMinutes = Math.floor(ms / 60000);
  return {
    days: Math.floor(totalMinutes / (60 * 24)),
    hours: Math.floor((totalMinutes % (60 * 24)) / 60),
    minutes: totalMinutes % 60,
  };
}

/** "Oct 31 at 11:59 PM" in Eastern time. */
export function formatCloseEt(closesAt: string): { date: string; time: string } {
  const d = new Date(closesAt);
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }),
  };
}

export type LifetimeCheckoutResult =
  | { ok: true; url: string }
  | { ok: false; reason: "closed" | "already_lifetime" | "unavailable" | "over_cap" | "error" | "sign_in"; maxUnits?: number; reference?: string };

export function parseEventId(raw: string | null | undefined): number | undefined {
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

export function lifetimeClaimPath(units: number, eventId?: number): string {
  return `/app/lifetime-claim?offer=syre-lifetime&units=${units}${eventId ? `&eventId=${eventId}` : ""}`;
}

export async function startLifetimeCheckout(units: number = 1, eventId?: number): Promise<LifetimeCheckoutResult> {
  try {
    const res = await fetch(LIFETIME_CHECKOUT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ units, ...(eventId ? { eventId } : {}) }),
    });
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok && typeof data?.url === "string") return { ok: true, url: data.url };
    if (res.status === 401) return { ok: false, reason: "sign_in" };
    const reason =
      data?.reason === "closed" || data?.reason === "already_lifetime" || data?.reason === "unavailable" || data?.reason === "over_cap"
        ? data.reason
        : "error";
    const reference = typeof data?.reference === "string" && /^[a-f0-9-]{36}$/.test(data.reference) ? data.reference : undefined;
    return { ok: false, reason, ...(reference ? { reference } : {}),
      ...(typeof data?.maxUnits === "number" ? { maxUnits: data.maxUnits } : {}) };
  } catch {
    return { ok: false, reason: "error" };
  }
}
